import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import { useAnchoredOverlay } from "../../../../hooks/useAnchoredOverlay";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import type { ChoiceRow } from "../../../../lib/forms/fieldOptions";
import { labeledChoiceFromRows } from "../../../../lib/forms/fieldOptions";
import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";
import ChoiceElement from "../choice-list/choice-element";

interface SelectInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  choiceRows: ChoiceRow[];
  disabled?: boolean;
  emptyHint?: string;
  validation?: (value: string) => boolean;
  inlineList?: boolean;
}

function filterRows(rows: ChoiceRow[], query: string): ChoiceRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => row.label.toLowerCase().includes(q));
}

function findRowByLabel(
  rows: ChoiceRow[],
  term: string,
): ChoiceRow | undefined {
  const normalized = term.trim().toLowerCase();
  return rows.find((row) => row.label.toLowerCase() === normalized);
}

const SelectInput = forwardRef<FormFieldRef, SelectInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const [value, setValue] = useState(props.initialValue ?? "");
  const [query, setQuery] = useState(
    () =>
      props.choiceRows.find((row) => row.value === props.initialValue)?.label ??
      "",
  );
  const inlineList = Boolean(props.inlineList);
  const [isOpened, setOpened] = useState(inlineList);
  const [validationState, setValidationState] = useState(true);
  const [err, setErr] = useState("");

  const selectedRow = useMemo(
    () => props.choiceRows.find((row) => row.value === value),
    [props.choiceRows, value],
  );

  const filteredRows = useMemo(
    () => filterRows(props.choiceRows, query),
    [props.choiceRows, query],
  );

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  useEffect(() => {
    if (
      value &&
      props.choiceRows.length > 0 &&
      !props.choiceRows.some((row) => row.value === value)
    ) {
      commitValue("");
      setQuery("");
      setOpened(false);
      setValidationState(true);
      setErr("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when options/value mismatch
  }, [props.choiceRows, value]);

  useEffect(() => {
    if (query || !selectedRow) return;
    setQuery(selectedRow.label);
  }, [query, selectedRow]);

  const clear = () => {
    commitValue("");
    setQuery("");
    setOpened(false);
    setValidationState(true);
    setErr("");
  };

  const validate = () => {
    if (!props.validation) return true;

    const result = props.validation(value);
    if (!result) setErr("Выберите значение из списка");
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    labeledChoice: labeledChoiceFromRows(props.choiceRows, value),
    validate,
    clear,
  }));

  const openList = () => {
    if (props.disabled) return;
    setOpened(true);
    setValidationState(true);
    setErr("");
  };

  const showList = !props.disabled && (inlineList || isOpened);

  const handleSelect = (row: ChoiceRow) => {
    setQuery(row.label);
    commitValue(row.value);
    setOpened(false);
    setValidationState(true);
    setErr("");
  };

  const handleFocus = () => {
    openList();
    if (!query && selectedRow) {
      setQuery(selectedRow.label);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setQuery(next);
    openList();

    const exact = findRowByLabel(props.choiceRows, next);
    if (exact) {
      commitValue(exact.value);
    } else if (value) {
      commitValue("");
    }
  };

  const handleOnBlur = () => {
    if (inlineList) return;
    if (overlayRef.current?.contains(document.activeElement)) return;
    setOpened(false);

    const exact = findRowByLabel(props.choiceRows, query);
    if (exact) {
      setQuery(exact.label);
      commitValue(exact.value);
      setValidationState(true);
      setErr("");
      return;
    }

    if (query.trim() === "") {
      commitValue("");
      setValidationState(true);
      setErr("");
      return;
    }

    if (selectedRow) {
      setQuery(selectedRow.label);
      setValidationState(true);
      setErr("");
      return;
    }

    setValidationState(false);
    setErr("Выберите значение из списка");
  };

  useClickOutside(
    containerRef,
    () => setOpened(false),
    showList && !inlineList,
    overlayRef,
  );
  useAnchoredOverlay({
    open: showList && !inlineList,
    triggerRef: inputRef,
    overlayRef,
    minHeight: 160,
    matchTriggerWidth: true,
  });

  const list = showList ? (
    <div
      ref={inlineList ? undefined : overlayRef}
      id={`${listId}-listbox`}
      onMouseDown={(event) => event.preventDefault()}
      className={
        inlineList
          ? "mt-1 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          : "fixed z-[80] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10"
      }
    >
      {filteredRows.length > 0 ? (
        <ul className="scrollbar-thin max-h-56 overflow-y-auto p-2" role="listbox">
          {filteredRows.map((row) => (
            <li key={`${row.value}-${row.label}`} role="presentation">
              <ChoiceElement
                title={row.label}
                handleClick={() => handleSelect(row)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 py-2.5 text-sm text-slate-500 font-onest">
          {props.emptyHint || "Ничего не найдено"}
        </p>
      )}
    </div>
  ) : null;

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={listId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl>
        <div ref={containerRef} className="relative">
          <input
            ref={inputRef}
            id={listId}
            autoComplete="off"
            value={query}
            onChange={handleChange}
            onBlur={handleOnBlur}
            onFocus={handleFocus}
            type="text"
            disabled={props.disabled}
            role="combobox"
            aria-expanded={showList}
            aria-controls={`${listId}-listbox`}
            aria-autocomplete="list"
            aria-invalid={!validationState}
            {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
            placeholder={props.emptyHint || "Начните вводить для поиска"}
            className={getInputClassName(
              validationState,
              props.disabled
                ? "cursor-not-allowed bg-slate-50 text-slate-400"
                : undefined,
            )}
          />
          {inlineList
            ? list
            : list
              ? createPortal(list, document.body)
              : null}
        </div>
      </FieldControl>
      <FieldFooter>
        {err ? (
          <span className="text-red-600" role="alert">
            {err}
          </span>
        ) : !validationState ? (
          <span className="text-red-600" role="alert">
            Выберите значение из списка
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

SelectInput.displayName = "SelectInput";

export { SelectInput };
export type { SelectInputProps };
